import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { CONTRACT_ADDRESSES } from "../contracts/addresses";
import { useEthers } from "./useEthers";
import StakingAbi from "../contracts/abis/StakingAbi.json";
import TokenAbi from "../contracts/abis/TokenAbi.json";

export function useStakingContract() {
  const { provider, account, getContract } = useEthers();
  
  // Estados para contratos
  const [stakingContract, setStakingContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);
  
  // Estados para balances y datos
  const [stakeableBalance, setStakeableBalance] = useState("0");
  const [stakedBalance, setStakedBalance] = useState("0");
  const [totalStaked, setTotalStaked] = useState("0");
  const [rewardRate, setRewardRate] = useState("108");
  const [stakingPaused, setStakingPaused] = useState(false);
  const [userStakeInfo, setUserStakeInfo] = useState(null);
  
  // Estados para UI
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [isStaking, setIsStaking] = useState(false);
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  
  // Inicializar contratos
  useEffect(() => {
    if (!provider) return;
    
    try {
      const staking = getContract(CONTRACT_ADDRESSES.staking, StakingAbi);
      const token = getContract(CONTRACT_ADDRESSES.token, TokenAbi);
      
      setStakingContract(staking);
      setTokenContract(token);
    } catch (error) {
      console.error("Error al inicializar contratos de staking:", error);
    }
  }, [provider, getContract]);
  
  // Cargar datos del contrato de staking
  useEffect(() => {
    const loadStakingData = async () => {
      if (!stakingContract || !account) {
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Método directo usando llamada a contrato
        console.log("⚡ Intentando cargar balance stakeado para:", account);
        console.log("⚡ Dirección del contrato:", CONTRACT_ADDRESSES.staking);
        
        // Enfoque 1: Usando método normal
        try {
          const staked = await stakingContract.balanceOf(account);
          console.log("⚡ Balance stakeado (método balanceOf):", staked.toString());
          console.log("⚡ En ETH:", ethers.utils.formatEther(staked));
          setStakedBalance(staked);
        } catch (err) {
          console.error("❌ Error con balanceOf:", err);
          
          // Enfoque 2: Usando método alternativo si existe
          try {
            if (stakingContract.stakes) {
              const stakeInfo = await stakingContract.stakes(account);
              if (stakeInfo && stakeInfo.amount) {
                console.log("⚡ Balance stakeado (método stakes):", stakeInfo.amount.toString());
                setStakedBalance(stakeInfo.amount);
              }
            }
          } catch (stakesErr) {
            console.error("❌ Error también con stakes:", stakesErr);
          }
        }
        
        // Intentar cargar el total stakeado
        try {
          let total;
          if (stakingContract.totalSupply) {
            total = await stakingContract.totalSupply();
            console.log("⚡ Total stakeado (totalSupply):", total.toString());
          } else if (stakingContract.totalStaked) {
            total = await stakingContract.totalStaked();
            console.log("⚡ Total stakeado (totalStaked):", total.toString());
          }
          setTotalStaked(total);
        } catch (err) {
          console.error("❌ Error al cargar total stakeado:", err);
        }

        // Resto del código existente...
        const [rate, paused] = await Promise.all([
          stakingContract.rewardRate(),
          stakingContract.stakingPaused ? stakingContract.stakingPaused() : Promise.resolve(false)
        ]);
        
        // Actualizar estados
        setRewardRate(rate.toString());
        setStakingPaused(paused);
        
        // Cargar info del usuario si existe la función
        if (stakingContract.getUserStakeInfo) {
          const info = await stakingContract.getUserStakeInfo(account);
          setUserStakeInfo(info);
        } else {
          // Fallback si no existe la función específica
          const stakeInfo = await stakingContract.stakes(account);
          setUserStakeInfo(stakeInfo);
        }
        
        setIsLoading(false);
        setLastUpdateTime(new Date());
      } catch (error) {
        console.error("Error general al cargar datos de staking:", error);
        setIsLoading(false);
      }
    };
    
    loadStakingData();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(loadStakingData, 30000);
    return () => clearInterval(interval);
  }, [stakingContract, account]);
  
  // Cargar balance stakeable del token
  useEffect(() => {
    const loadStakeableBalance = async () => {
      if (!tokenContract || !account) {
        setStakeableBalance("0");
        return;
      }
      
      try {
        const balance = await tokenContract.balanceOf(account);
        setStakeableBalance(balance.toString());
      } catch (error) {
        console.error("Error al cargar balance stakeable:", error);
        setStakeableBalance("0");
      }
    };
    
    loadStakeableBalance();
    
    // Actualizar cada 15 segundos
    const interval = setInterval(loadStakeableBalance, 15000);
    return () => clearInterval(interval);
  }, [tokenContract, account]);
  
  // Función para refrescar todos los datos
  const refreshData = useCallback(async () => {
    if (!stakingContract || !account) return;
    
    try {
      // Cargar datos del contrato de staking
      const [staked, total, rate] = await Promise.all([
        stakingContract.balanceOf(account),
        stakingContract.totalSupply ? stakingContract.totalSupply() : stakingContract.totalStaked(),
        stakingContract.rewardRate()
      ]);
      
      setStakedBalance(staked.toString());
      setTotalStaked(total.toString());
      setRewardRate(rate.toString());
      
      // Cargar info del usuario
      if (stakingContract.getUserStakeInfo) {
        const info = await stakingContract.getUserStakeInfo(account);
        setUserStakeInfo(info);
      } else {
        const stakeInfo = await stakingContract.stakes(account);
        setUserStakeInfo(stakeInfo);
      }
      
      // Actualizar balance stakeable
      if (tokenContract) {
        const balance = await tokenContract.balanceOf(account);
        setStakeableBalance(balance.toString());
      }
      
      setLastUpdateTime(new Date());
    } catch (error) {
      console.error("Error al refrescar datos de staking:", error);
    }
  }, [stakingContract, tokenContract, account]);
  
  // Calcular recompensas pendientes
  const calculateRewards = useCallback(async () => {
    if (!stakingContract || !account) return "0";
    
    try {
      const rewards = await stakingContract.calculateReward(account);
      return rewards.toString();
    } catch (error) {
      console.error("Error al calcular recompensas:", error);
      return "0";
    }
  }, [stakingContract, account]);
  
  // Función para stake
  const stake = useCallback(async (amount) => {
    if (!stakingContract || !tokenContract || !account) {
      throw new Error("Contratos no disponibles o wallet no conectada");
    }
    
    if (!amount || amount <= 0) {
      throw new Error("Cantidad inválida para stake");
    }
    
    try {
      setIsStaking(true);
      
      // Primero aprobar el gasto de tokens
      const approveTx = await tokenContract.approve(CONTRACT_ADDRESSES.staking, amount);
      await approveTx.wait();
      
      // Luego hacer el stake
      const tx = await stakingContract.stake(amount);
      const receipt = await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsStaking(false);
      return receipt;
    } catch (error) {
      setIsStaking(false);
      console.error("Error al stakear:", error);
      throw error;
    }
  }, [stakingContract, tokenContract, account, refreshData]);
  
  // Función para unstake (simplificada para llamar directamente a withdraw)
  const unstake = useCallback(async () => {
    if (!stakingContract || !account) {
      throw new Error("Contrato no disponible o wallet no conectada");
    }
    
    try {
      setIsUnstaking(true);
      
      // Llamar directamente a withdraw sin verificación adicional
      const tx = await stakingContract.withdraw();
      await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsUnstaking(false);
      return true;
    } catch (error) {
      setIsUnstaking(false);
      console.error("Error al retirar tokens:", error);
      throw error;
    }
  }, [stakingContract, account, refreshData]);
  
  // Función para finalizar unstake (si es un proceso en dos pasos)
  const finalizeUnstake = useCallback(async () => {
    if (!stakingContract || !account) {
      throw new Error("Contrato no disponible o wallet no conectada");
    }
    
    try {
      setIsUnstaking(true);
      
      // Finalizar el proceso de unstake
      const tx = await stakingContract.finalizeUnstake();
      const receipt = await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsUnstaking(false);
      return receipt;
    } catch (error) {
      setIsUnstaking(false);
      console.error("Error al finalizar unstake:", error);
      throw error;
    }
  }, [stakingContract, account, refreshData]);
  
  // Función para reclamar recompensas
  const claimRewards = useCallback(async () => {
    if (!stakingContract || !account) {
      throw new Error("Contrato no disponible o wallet no conectada");
    }
    
    try {
      setIsClaiming(true);
      
      // Reclamar recompensas
      const tx = await stakingContract.claimRewards();
      const receipt = await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsClaiming(false);
      return receipt;
    } catch (error) {
      setIsClaiming(false);
      console.error("Error al reclamar recompensas:", error);
      throw error;
    }
  }, [stakingContract, account, refreshData]);
  
  // Función para re-stakear recompensas
  const compoundRewards = useCallback(async () => {
    if (!stakingContract || !account) {
      throw new Error("Contrato no disponible o wallet no conectada");
    }
    
    try {
      setIsClaiming(true);
      
      // Re-stakear recompensas
      const tx = await stakingContract.reStakeRewards();
      const receipt = await tx.wait();
      
      // Refrescar datos
      await refreshData();
      
      setIsClaiming(false);
      return receipt;
    } catch (error) {
      setIsClaiming(false);
      console.error("Error al restakear recompensas:", error);
      throw error;
    }
  }, [stakingContract, account, refreshData]);
  
  // Verificar si el usuario puede finalizar un unstake
  const canFinalizeUnstake = useCallback(() => {
    if (!userStakeInfo || !userStakeInfo.lockEndTime) return false;
    
    const now = Math.floor(Date.now() / 1000);
    const lockEndTime = userStakeInfo.lockEndTime.toNumber();
    
    return lockEndTime > 0 && now >= lockEndTime;
  }, [userStakeInfo]);
  
  // Calcular tiempo restante hasta que se pueda finalizar el unstake
  const getTimeUntilUnstake = useCallback(() => {
    if (!userStakeInfo || !userStakeInfo.lockEndTime) return 0;
    
    const now = Math.floor(Date.now() / 1000);
    const lockEndTime = userStakeInfo.lockEndTime.toNumber();
    
    if (lockEndTime === 0) return 0; // No hay unstake iniciado
    
    return Math.max(0, lockEndTime - now);
  }, [userStakeInfo]);
  
  return {
    contract: stakingContract,
    stakedBalance: stakedBalance || "0",
    stakeableBalance,
    totalStaked: totalStaked || "0",
    rewardRate: rewardRate || "108",
    userStakeInfo,
    stakingPaused,
    isLoading,
    isStaking,
    isUnstaking,
    isClaiming,
    calculateRewards,
    stake,
    unstake,
    finalizeUnstake,
    claimRewards,
    compoundRewards,
    refreshData,
    canFinalizeUnstake,
    getTimeUntilUnstake,
    lastUpdateTime
  };
}
